import { useLocation, useSearchParams } from "react-router-dom";

/** The param, exported so the layout, the page and the tests all name the same string. */
export const DENSITY_PARAM = "board:density";

export type BoardDensity = "normal" | "condensed";

/**
 * How much of an opportunity a board cell draws.
 *
 * `normal` is the card: five facts, three rows, a capped cell with "+ N more". `condensed` is
 * a chip: the client's name in the owner's colour, uncapped, wrapping so several sit in a row.
 * The client asked for the second one to fit a whole pipeline on a screen — see
 * [condensed-density](../../../../../specs/board/condensed-density.md).
 *
 * **`board:`, not `opportunities:`.** The latter is the filter namespace the board and the list
 * *share*, which is what makes the view toggle preserve a filter set. Density is the opposite:
 * it means nothing in list view, so it must not travel in the namespace of things that do.
 *
 * A sibling of `useTvMode` rather than part of `usePipelineScope`, for the same reason that hook
 * is: `AppLayout` needs the answer to decide the page's width and must not pay for the year
 * window's query to get it.
 *
 * Pipeline route only. A `board:density` param carried onto the client registry by a stray link
 * must not narrow that screen — exactly as `board:tv` must not strip its navigation.
 */
export function useBoardDensity(): { density: BoardDensity; setDensity: (next: BoardDensity) => void } {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const onPipeline = location.pathname === "/";
  const density: BoardDensity =
    onPipeline && searchParams.get(DENSITY_PARAM) === "condensed" ? "condensed" : "normal";

  const setDensity = (next: BoardDensity): void => {
    // See useFilterParam — read the live URL, not react-router's render-time snapshot.
    const params = new URLSearchParams(window.location.search);
    // Deleted at the default, like every other param: a normal board has a clean URL, and a
    // condensed one is a link somebody can send.
    if (next === "condensed") params.set(DENSITY_PARAM, "condensed");
    else params.delete(DENSITY_PARAM);
    setSearchParams(params, { replace: true });
  };

  return { density, setDensity };
}
